import { isEqual } from 'lodash'
import { makeAutoObservable, runInAction, toJS } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { Icon, toastStore } from 'shared/ui'
import { LINK_SMART_VIEWS_FILTERS } from 'shared/constants/links'
import { RequestCancelAxios } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { type SmartView } from 'entities/SmartViews/model/SmartView'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseSmartView } from 'entities/SmartViews/api/types'
import { SmartViewsApi } from 'entities/SmartViews/api/smartViews'
import { FilterEditorStore } from 'widgets/FilterEditor'
import { SmartViewModalContent } from 'widgets/SmartViewModal/ui/SmartViewModalContent'
import { SmartViewModalActions } from 'widgets/SmartViewModal/ui/SmartViewModalActions'

type ISmartViewModalStoreConfig = {
  onAdd: (item: SmartView) => void
  onEdit: (id: number) => void
}

export class SmartViewModalStore {
  private _title = ''
  private _errorTitle = ''
  private _item: SmartView | null = null
  private _loading = false
  private _idModal = 'smart_view_modal'
  private _config: ISmartViewModalStoreConfig | null = null
  private _isEdit = false
  private _loadingSubmit = false
  readonly filterEditorStore: FilterEditorStore
  private _cancelRequest = new RequestCancelAxios()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.filterEditorStore = new FilterEditorStore(
      {
        makeRequest: () => {
          this._cancelRequest.init()

          return SmartViewsApi.getSmartViewFilters({
            cancelToken: this._cancelRequest.token,
          }).then(({ data }) => data)
        },
        showSpawned: true,
        mutateFilterGroups: (groups, filterGroups) => {
          return [...filterGroups, this.pinnedGroup]
        },
        placeholderPrefixMap: {
          'conversations.user_id': 'assignees',
          'conversations.team_id': 'inboxes',
        },
      },
      this._entities
    )
  }

  get pinnedGroup() {
    return {
      label: '',
      category: 'pinned',
      items: [
        {
          id: 'new',
          label: 'Request a filter',
          customIconL: <Icon icon={'plusCircle'} color={'var(--content-brand-primary)'} />,
          onClick: () => {
            window.open(LINK_SMART_VIEWS_FILTERS, '_blank')
          },
        },
      ],
    }
  }

  setConfig = (config: ISmartViewModalStoreConfig) => {
    this._config = config
  }

  private _loadFilters = () => this.filterEditorStore.initFilters()

  private _setItem = (item: SmartView) => {
    this._item = item
    this._title = item.name

    if (item.filters.length) {
      this.filterEditorStore.groups.populateSegmentFilters(item.filters)
    }
  }

  setTitle = (value: string) => {
    this._title = value

    this.setError()
  }

  setError = () => {
    const value = this._title

    this._errorTitle = ''

    if (!value) {
      this._errorTitle = 'Enter smart view name'
    }

    if (value.length > 80) {
      this._errorTitle = '80-character limit reached'
    }
  }

  dispose = () => {
    this._title = ''
    this._errorTitle = ''
    this._item = null
    this._loading = false
    this._isEdit = false
    this._loadingSubmit = false
    this.filterEditorStore.dispose()
    this._cancelRequest.reset()
    modalStore.removeModal(this._idModal)
  }

  onSubmit = async () => {
    try {
      const data = this.filterEditorStore.groups.toSegmentFilters()
      let responseItem: IResponseSmartView

      runInAction(() => {
        this._loadingSubmit = true
      })

      if (this._item) {
        const response = await SmartViewsApi.updateSmartView(this._item.id, {
          name: this._title,
          filters: data.filtersList,
        })
        responseItem = response.data
      } else {
        const response = await SmartViewsApi.createSmartView({
          name: this._title,
          filters: data.filtersList,
        })
        responseItem = response.data

        this._entities.eventLogStore.logEvent(
          'Smart View Created',
          {
            event_id: 'smartview_created',
            appliedFilters: JSON.stringify(data.filtersList),
          },
          { groupId: this._entities.organizationStore.id }
        )
      }

      const item = this._entities.smartViewsStore.addItem(responseItem)

      if (item) {
        this._config?.onAdd(item)
      }

      if (this._isEdit && item) {
        this._config?.onEdit(item.id)
      }

      toastStore.add({
        title: this._isEdit ? 'Smart view saved' : 'Smart view created',
        type: 'success',
      })

      this.onCloseModal()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingSubmit = false
      })
    }
  }

  onCloseModal = () => {
    modalStore.removeModal(this._idModal)
    this.dispose()
  }

  openModal = (item?: SmartView) => {
    this._isEdit = Boolean(item)

    this._loadFilters().then(() => {
      if (item) {
        this._setItem(item)
      }
    })

    modalStore.addModal({
      id: this._idModal,
      title: this.titleModal,
      width: 800,
      ModalActions: SmartViewModalActions,
      ModalContent: SmartViewModalContent,
      ModalContentProps: {
        store: this,
      },
      onClose: this.onCloseModal,
      paddingContent: '0px 24px 16px 24px',
    })
  }

  get titleModal() {
    if (this.editMode) return 'Edit smart view'

    return 'Create smart view'
  }

  get editMode() {
    return this._isEdit
  }

  get loading() {
    return this._loading || this.filterEditorStore.loading
  }

  get disabled() {
    const base = Boolean(
      this.loading ||
        this._errorTitle ||
        !this._title ||
        !this.filterEditorStore.groups.countWithValue ||
        this._loadingSubmit
    )

    if (this._isEdit) return base || (!this.hasChangedName && !this.hasChangedFilter)

    return base
  }

  get hasChangedName() {
    if (!this._item) return false

    return this._item.name !== this._title
  }

  get hasChangedFilter() {
    if (!this._item) return false

    const origin = toJS(this._item.filters)
    const modified = this.filterEditorStore.groups.toSegmentFilters().filtersList

    return !isEqual(origin, modified)
  }

  get loadingSubmit() {
    return this._loadingSubmit
  }

  get title() {
    return this._title
  }

  get errorTitle() {
    return this._errorTitle
  }

  get titleButton() {
    if (this._isEdit) return 'Save changes'

    return 'Create'
  }
}
